/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 settings layout (design reference "Settings · Overview" and "Settings ·
 * Phone and softphone"): a settings menu card beside every /settings page, so
 * people move between sections without going back to the overview. The page
 * itself renders unchanged on the right.
 *
 * The menu lists the sections useSettingsSections returns, so it hides and
 * locks exactly what the overview cards do. Its search box also filters the
 * overview cards (SettingsQueryContext). Below 1200px it folds into a button
 * that opens the same menu in a drawer.
 */

import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha } from "@mui/material/styles";
import { Lock, Menu as MenuIcon, Search, X } from "lucide-react";
import { useAuth } from "auth/Auth";
import { v2 as t } from "styles/tokens/v2";
import useSettingsSections from "./useSettingsSections";
import { SettingsQueryProvider } from "./SettingsQueryContext";
import { activeNavKeys, buildSettingsNav, filterSettingsNav } from "./settingsNav";

const tint = alpha(t.primary, 0.1);

// The page column is narrower beside the menu; buttons keep their label on
// one line and the text beside them wraps instead.
const contentSx = { minWidth: 0, "& .MuiButton-root": { whiteSpace: "nowrap", flexShrink: 0 } };

const rowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 1.25,
  width: "100%",
  minHeight: 38,
  px: 1.25,
  borderRadius: "8px",
  fontFamily: "inherit",
  fontSize: 14,
  textAlign: "left",
  textDecoration: "none",
  color: t.text,
  position: "relative",
  "&:hover": { bgcolor: t.hover },
  "&:focus-visible": { outline: `2px solid ${t.link}`, outlineOffset: 1 },
};

const activeSx = {
  bgcolor: tint,
  color: t.primaryDark,
  fontWeight: 600,
  "&:hover": { bgcolor: tint },
  "&::before": { content: '""', position: "absolute", left: -10, top: 7, bottom: 7, width: 3, borderRadius: 3, bgcolor: t.primary },
};

function NavItem({ item, active, activeChild, expanded, onNavigate }) {
  const Icon = item.icon;
  if (!item.to) {
    return (
      <Tooltip title={item.blockedReason || ""} placement="right">
        <Box component="li" aria-disabled="true" sx={{ ...rowSx, color: t.textDisabled, cursor: "not-allowed", "&:hover": {} }}>
          <Icon size={17} aria-hidden="true" />
          <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{item.title}</Box>
          <Lock size={14} aria-label={item.blockedReason || "Locked"} />
        </Box>
      </Tooltip>
    );
  }
  const subItems = item.matches?.length ? item.matches : expanded ? item.children : [];
  return (
    <li>
      <ButtonBase
        component={RouterLink}
        to={item.to}
        onClick={onNavigate}
        aria-current={active && !activeChild ? "page" : undefined}
        sx={{ ...rowSx, ...(active ? activeSx : null) }}
      >
        <Icon size={17} aria-hidden="true" color={active ? t.primary : t.text2} />
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.title}
        </Box>
      </ButtonBase>
      {subItems.length > 0 && (
        <Box component="ul" sx={{ listStyle: "none", m: "2px 0 4px", p: 0, pl: 3.75, borderLeft: `1px solid ${t.border}`, ml: 2.25 }}>
          {subItems.map((c) => {
            const on = c.key === activeChild;
            return (
              <li key={c.key}>
                <ButtonBase
                  component={RouterLink}
                  to={c.to}
                  onClick={onNavigate}
                  aria-current={on ? "page" : undefined}
                  sx={{ ...rowSx, minHeight: 32, fontSize: 13.5, color: on ? t.primaryDark : t.text2, fontWeight: on ? 600 : 400, bgcolor: on ? tint : "transparent", "&:hover": { bgcolor: on ? tint : t.hover } }}
                >
                  {c.label}
                </ButtonBase>
              </li>
            );
          })}
        </Box>
      )}
    </li>
  );
}

const navItemShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  to: PropTypes.string,
  blockedReason: PropTypes.string,
  children: PropTypes.array,
  matches: PropTypes.array,
});

NavItem.propTypes = {
  item: navItemShape.isRequired,
  active: PropTypes.bool.isRequired,
  activeChild: PropTypes.string,
  expanded: PropTypes.bool.isRequired,
  onNavigate: PropTypes.func,
};
NavItem.defaultProps = { activeChild: null, onNavigate: undefined };

function SettingsMenu({ groups, query, setQuery, onNavigate }) {
  const { pathname, search } = useLocation();
  const searching = query.trim().length > 0;
  const shown = useMemo(() => filterSettingsNav(groups, query), [groups, query]);
  const active = useMemo(() => activeNavKeys(groups, pathname, search), [groups, pathname, search]);

  return (
    <Box component="nav" aria-label="Settings" sx={{ p: 1.25 }}>
      <TextField
        size="small"
        type="search"
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setQuery("");
        }}
        placeholder="Search settings"
        inputProps={{ "aria-label": "Search settings", autoComplete: "off" }}
        sx={{ mb: 1, "& input::-webkit-search-cancel-button": { display: "none" } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={16} color={t.text3} aria-hidden="true" />
            </InputAdornment>
          ),
          endAdornment: query ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear search" onClick={() => setQuery("")} sx={{ p: 0.25 }}>
                <X size={14} />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      {searching && shown.length === 0 && (
        <Typography role="status" sx={{ px: 1.25, py: 2, fontSize: 13.5, color: t.text2 }}>
          No settings match “{query.trim()}”.
        </Typography>
      )}
      {shown.map((g) => (
        <Box key={g.key} sx={{ mt: 1.5, px: 1.25 }}>
          <Typography component="h2" sx={{ px: 1.25, mb: 0.5, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: t.text3 }}>
            {g.label}
          </Typography>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: "2px" }}>
            {g.items.map((it) => (
              <NavItem
                key={it.key}
                item={it}
                active={it.key === active.item}
                activeChild={it.key === active.item ? active.child : null}
                expanded={!searching && it.key === active.item}
                onNavigate={onNavigate}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

SettingsMenu.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, items: PropTypes.arrayOf(navItemShape) })).isRequired,
  query: PropTypes.string.isRequired,
  setQuery: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
};
SettingsMenu.defaultProps = { onNavigate: undefined };

export default function SettingsLayoutV2({ children }) {
  // The menu beside the page needs room: below 1200px pages keep their full
  // width and the menu opens from a button instead.
  const narrow = useMediaQuery("(max-width:1199.95px)");
  const { axiosInstance } = useAuth();
  const { pathname, search } = useLocation();
  const { settingsSections, isAvailableInPlayground } = useSettingsSections();
  const [query, setQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isPlayground, setIsPlayground] = useState(false);

  // Playground tenants open only some sections; the cards grey the rest out,
  // and so does the menu.
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      ?.get("/company/info")
      .then((res) => {
        if (!cancelled) setIsPlayground(res.data?.is_playground === true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [axiosInstance]);

  // Opening a section clears the search, so the menu goes back to showing
  // where you are.
  useEffect(() => {
    setQuery("");
    setDrawerOpen(false);
  }, [pathname, search]);

  const groups = useMemo(
    () =>
      buildSettingsNav(settingsSections, {
        blocked: (s) =>
          s.lockedReason || (isPlayground && !isAvailableInPlayground(s.title) ? "Not available in playground mode" : null),
      }),
    // settingsSections is rebuilt on every render; its titles and locks are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingsSections.map((s) => `${s.title}:${s.lockedReason || ""}`).join("|"), isPlayground]
  );

  const shared = useMemo(() => ({ query, setQuery }), [query]);
  const menu = <SettingsMenu groups={groups} query={query} setQuery={setQuery} />;

  return (
    <SettingsQueryProvider value={shared}>
      {narrow ? (
        <>
          <ButtonBase
            onClick={() => setDrawerOpen(true)}
            aria-haspopup="dialog"
            sx={{ ...rowSx, width: "auto", display: "inline-flex", mb: 1.5, border: `1px solid ${t.border}`, bgcolor: t.surface, fontWeight: 600 }}
          >
            <MenuIcon size={16} aria-hidden="true" />
            Settings menu
          </ButtonBase>
          <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 300, bgcolor: t.surface } }}>
            <SettingsMenu groups={groups} query={query} setQuery={setQuery} onNavigate={() => setDrawerOpen(false)} />
          </Drawer>
          {children}
        </>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "264px minmax(0, 1fr)", gap: 2.5, alignItems: "start" }}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              mt: 1,
              maxHeight: "calc(100vh - var(--v2-frame-offset, 116px) - 8px)",
              overflowY: "auto",
              bgcolor: t.surface,
              border: `1px solid ${t.border}`,
              borderRadius: "12px",
              boxShadow: t.shadow,
            }}
          >
            {menu}
          </Box>
          <Box sx={contentSx}>{children}</Box>
        </Box>
      )}
    </SettingsQueryProvider>
  );
}

SettingsLayoutV2.propTypes = { children: PropTypes.node.isRequired };
